import { Component, Input } from '@angular/core';
import { CommonModule, DecimalPipe, AsyncPipe } from '@angular/common';
import { CellRenderer, CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';
import { Observable } from 'rxjs';
import { WorkProgressMaterialItem } from '../../interfaces/work-progress-material.interface';

export interface WorkProgressQuantityCellRendererParams {
  UMPActive$: Observable<boolean>;
}

/**
 * Work Progress Quantity Cell Renderer
 *
 * Cell renderer específico para Work Progress Material Summary.
 * Usa los nombres de campos CORRECTOS (parallelQuantity, parallelUnitOfMeasure)
 * sin depender de typos heredados.
 *
 * Displays quantity information with:
 * - Base quantity (UMB) with unit
 * - Parallel quantity (UMP) with unit (if UMPActive$ observable emits true)
 *
 * @example
 * ```typescript
 * {
 *   colId: 'quantity',
 *   headerName: 'Cantidad',
 *   cellRenderer: WorkProgressQuantityCellRendererComponent,
 *   cellRendererParams: {
 *     UMPActive$: this.UMPActive$
 *   },
 *   minWidth: 200
 * }
 * ```
 */
@Component({
  selector: 'app-work-progress-quantity-cell-renderer',
  standalone: true,
  imports: [CommonModule, AsyncPipe, DecimalPipe],
  template: `
    <div class="table-text-m-2 text-sm">
      <div class="mb-1">
        <b>UMB:</b> {{ params?.data?.baseQuantity | number: '1.3' }}
        <span class="text-gray-500">
          ({{ params?.data?.baseUnitOfMeasure?.name || '-' }})
        </span>
      </div>

      <div *ngIf="(customParams?.UMPActive$ | async)">
        <b>UMP:</b>
        <ng-container *ngIf="params?.data?.parallelUnitOfMeasure; else noUMP">
          {{ params?.data?.parallelQuantity | number: '1.3' }}
          <span class="text-gray-500">
            ({{ params?.data?.parallelUnitOfMeasure?.name }})
          </span>
        </ng-container>
        <ng-template #noUMP>
          <span class="text-gray-500">-</span>
        </ng-template>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
  `]
})
export class WorkProgressQuantityCellRendererComponent implements CellRenderer<WorkProgressMaterialItem> {
  @Input() params!: CellRendererParams<WorkProgressMaterialItem>;

  get customParams(): WorkProgressQuantityCellRendererParams | undefined {
    return this.params?.colDef?.cellRendererParams as WorkProgressQuantityCellRendererParams;
  }
}
